/**
 * Every sentence this application says about a failure, and the chain that picks one.
 *
 * ```
 * platform.<screen>.<code>   →   <code>   →   unknown
 * ```
 *
 * docs/ux/PLATFORM-ADMIN.md §20.1. Three rules hold it together, and each of them is a
 * defect in the register if it slips:
 *
 *   - **The API's own `message` is never rendered** — not in a notice, not in a field
 *     error, not appended to a sentence (**MF6**, **LB4**). Copy is chosen by `code`.
 *   - **An unrecognised code still produces a sentence a human can read.** Never a blank
 *     notice, never the code itself, never `undefined`.
 *   - **One language: English, written here** rather than in `packages/i18n`
 *     (docs/DECISIONS.md, "The platform admin has no design system, no theme and one
 *     language"). This app has one reader, who chose the software.
 *
 * A later screen adds its own entry to `SCREEN_MESSAGES` when a code means something
 * different there. P01 ships the root catalogue and the sign-in screen; P02–P04 add
 * `tenantList`, `createTenant`, `handoff`, `settings`, `branding`, `flags`.
 *
 * **`keys` and `buildIdentifier` are gone.** Both belonged to P03's signing-key screens,
 * built against the signed, `kid`-rotated build identifier; the plain `tenants.build_key`
 * column that replaced it (`docs/DECISIONS.md`, "The build identifier is a plain rotatable
 * key on the tenant row, not a signed, kid-rotated token") has no platform-wide keys to
 * list, mint or revoke, and rotating it is a `tenant`-screen action now.
 */

/** The screens the chain knows, camelCase and singular, from §3's inventory. */
export type Screen =
  | 'signIn'
  | 'account'
  | 'tenantList'
  | 'createTenant'
  | 'handoff'
  | 'tenant'
  | 'settings'
  | 'branding'
  | 'flags'

/**
 * The root: one sentence per code the API can produce (§20.2) and per code this client
 * produces for itself (§20.3).
 */
export const CODE_MESSAGES: Readonly<Record<string, string>> = {
  auth_invalid_credentials: 'Those credentials were not accepted.',
  auth_credentials_required: 'Your session has ended. Sign in again.',
  unauthenticated: 'Your session has ended. Sign in again.',
  auth_token_expired: 'Your session has ended. Sign in again.',
  auth_token_invalid: 'Your session has ended. Sign in again.',
  auth_token_wrong_type:
    'Your session has ended. Sign in again, and report this — the console sent the wrong kind of token.',
  validation_failed: 'Some of what was entered was not accepted. Check the fields marked below.',
  bad_request:
    'The console asked for something the API refused. Try again, and report it if it repeats.',
  forbidden:
    'This account cannot perform that action. Report it — every platform account has the same powers.',
  not_found: 'That is no longer there. It may have been removed since this page was loaded.',
  conflict:
    'Somebody changed this while you were working on it. Look at it again and redo the change.',
  slug_taken:
    'That slug is already taken. A slug is never released, even when the tenant that used it has been retired.',
  // P02's second code. It is not a failure of the request so much as a description of the
  // state it left behind, and its whole point is that the recovery is *not* a retry. It
  // covers two states — no superadmin, or a superadmin with no link — because the repair
  // differs and the tenant's own page is what says which, so the sentence sends the owner
  // there rather than asserting one of them.
  tenant_without_superadmin:
    'The tenant was created and nobody can sign in to it yet: either its first administrator was not created, or their activation link was not. Open the tenant — its page shows which — and create a superadmin, or mint a link for the one that is listed. Do not create the tenant again.',
  payload_too_large: 'That file is too large.',
  rate_limited: 'Too many attempts. Wait a moment before trying again.',
  server_misconfigured:
    'This deployment is missing a secret it needs for that. Fixing it is an operator action against the deployment.',
  internal_error: 'The API failed to answer that. Try again.',
  unavailable: 'The API is not available right now. Try again in a moment.',

  // §20.3 — failures that never reach the network, so nobody looks for them in the
  // OpenAPI document.
  client_offline: 'The API could not be reached. Check the connection and try again.',
  client_timeout: 'The API did not answer in time. Try again.',
  client_clipboard_denied:
    'The browser refused to copy that. Select the value and copy it by hand — it is still on screen.',
  client_media_too_large: 'That file is too large to upload.',
  client_media_unsupported: 'That file type cannot be used here.',
  client_unknown: 'Something went wrong. Try again, and report it if it repeats.',

  // §4.1 — this app sends no build identifier, so these are a bug in the client rather
  // than a state to design for. They still get a sentence, because a blank notice is
  // worse than an honest one.
  build_identifier_required: 'The console sent a malformed request. Report this.',
  build_identifier_invalid: 'The console sent a malformed request. Report this.',
  build_identifier_revoked: 'The console sent a malformed request. Report this.',
}

/** Where a code means something different on one screen than it does everywhere else. */
export const SCREEN_MESSAGES: Readonly<Partial<Record<Screen, Readonly<Record<string, string>>>>> =
  {
    // §20.4, in full. This is the one place `slug_taken` gets a longer sentence than the
    // root's, because the create screen is where the copy has to survive a third
    // possibility: the tenant holding this slug may be the one this owner created seconds
    // ago and never saw the response for (§8.4). So it names the rule, offers the lookup,
    // and never says "choose another" — sending them to a different slug in that state is
    // the one unrecoverable mistake this screen can cause.
    createTenant: {
      slug_taken:
        'That slug is already in use, and a slug is never released — not by retiring the tenant that holds it, and not by anything this console can do. Look it up before choosing another: it may be a tenant you have just created.',
      client_timeout:
        'The request was sent and nothing came back, so it may have worked. Look this slug up in the tenant list before trying again — if the tenant is there, open it and create its administrator from its own page.',
      validation_failed:
        'Some of what was entered was not accepted. Check the slug, the timezone and the languages.',
      conflict:
        'That was refused by the state the platform is already in. Look the slug up in the tenant list.',
    },

    tenantList: {
      not_found: 'That tenant is no longer here.',
    },

    tenant: {
      not_found: 'That tenant is no longer here.',
      conflict:
        'That is not possible in this tenant’s current state — most likely it has been retired since this page was loaded. Reload it.',
    },

    settings: {
      conflict:
        'This tenant was retired while the change was in flight, so nothing was saved. A retired tenant is not configured.',
      validation_failed:
        'Some of what was entered was not accepted. A timezone must be an IANA name such as Europe/Berlin, and the default language must be one of the available ones.',
    },

    handoff: {
      client_clipboard_denied:
        'The browser refused to copy that. Reveal the link and select it by hand — it is still on screen.',
    },

    // `branding.server_misconfigured` used to have its own sentence here, naming "no live
    // signing key" as the likely cause — the signing key that generated a build identifier
    // per request. There is no signing key any more (`docs/DECISIONS.md`, "The build
    // identifier is a plain rotatable key on the tenant row, not a signed, kid-rotated
    // token"): the branding document's `buildKey` is copied verbatim from the tenant row,
    // so a 500 generating it has no key-shaped cause left to name, and falls through to the
    // root's generic "missing a secret" sentence.
    branding: {
      not_found: 'That tenant is no longer there.',
      validation_failed:
        'Some of what was entered was not accepted. The brand colour must be 1–64 characters, and the logo URL must start with https:// or be left blank.',
      conflict:
        'This tenant was retired while the change was in flight, so nothing was saved. A retired tenant’s branding is read-only.',
    },

    flags: {
      not_found: 'That tenant is no longer there.',
      conflict: 'This tenant is retired, so its feature flags are read-only.',
    },

    signIn: {
      // One message for every failure: a wrong nickname, a wrong password and a disabled
      // account are indistinguishable, which is A01's `auth_invalid_credentials`
      // behaving exactly as designed (§4.2). The 401 a *tenant* user's credentials get
      // here is this same sentence — the screen never says that an account exists
      // elsewhere (§4.3).
      auth_invalid_credentials: 'Those credentials were not accepted.',
      unauthenticated: 'Those credentials were not accepted.',
      auth_credentials_required: 'Those credentials were not accepted.',
      auth_token_invalid: 'Those credentials were not accepted.',
      validation_failed: 'Enter a nickname and a password.',
      rate_limited:
        'Too many sign-in attempts. Wait a moment, then change something and try again.',
    },
  }

/** The last resort. Reached only by a code nothing in this application knows. */
export const UNKNOWN_MESSAGE =
  'Something went wrong that this console does not recognise. Try again, and report it if it repeats.'

/**
 * The sentence for one failure on one screen, resolved through the chain above.
 *
 * Total by construction: every path ends at `UNKNOWN_MESSAGE`, so no caller ever has to
 * decide what to draw when a code is missing.
 */
export function messageFor(screen: Screen, code: string): string {
  return SCREEN_MESSAGES[screen]?.[code] ?? messageForCode(code)
}

/**
 * The same chain from its second link, for the few failures that belong to no screen —
 * the shell's own reads, which are chrome rather than a page.
 */
export function messageForCode(code: string): string {
  return CODE_MESSAGES[code] ?? UNKNOWN_MESSAGE
}

/**
 * Codes that mean the session is over: the tokens are cleared and the owner lands on
 * `/login` with one line (§18.2).
 *
 * **`forbidden` is deliberately not here.** A 403 on a surface with one role is a
 * bug-shaped state, and signing the owner out over it would turn a server-side mistake
 * into a lockout.
 */
const SESSION_ENDING_CODES: ReadonlySet<string> = new Set([
  'unauthenticated',
  'auth_credentials_required',
  'auth_token_expired',
  'auth_token_invalid',
  'auth_token_wrong_type',
])

export function endsTheSession(code: string): boolean {
  return SESSION_ENDING_CODES.has(code)
}
